import { useMutation, useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import PersonAdd from "@mui/icons-material/PersonAdd";
import { Button, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { StaffRoleNamesEnum } from "encamp-shared/src/constants/permissionsTypes";
import { gql } from "generated-graphql";
import { StaffPermission } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import GET_STAFF_ROLES_QUERY, { StaffRoleRow } from "queries/getStaffRoles";
import { useMemo, useState } from "react";

import { AssignStaffUsersDialog } from "./AssignStaffUsersDialog";
import { DeleteRoleConfirmDialog, permissionsCountColumn, roleGridHeight } from "./roleGridShared";
import { StaffRoleEditorDialog, StaffRoleSaveInput } from "./StaffRoleEditorDialog";

const CREATE_STAFF_ROLE = gql(`
  mutation CreateStaffRole($name: String!, $description: String, $permissions: [StaffPermission!]!) {
    createStaffRole(name: $name, description: $description, permissions: $permissions) {
      id
      name
      description
      permissions
    }
  }
`);

const UPDATE_STAFF_ROLE = gql(`
  mutation UpdateStaffRole(
    $id: ID!
    $name: String!
    $description: String
    $permissions: [StaffPermission!]!
  ) {
    updateStaffRole(id: $id, name: $name, description: $description, permissions: $permissions) {
      id
      name
      description
      permissions
    }
  }
`);

const DELETE_STAFF_ROLE = gql(`
  mutation DeleteStaffRole($id: ID!) {
    deleteStaffRole(id: $id)
  }
`);

export function StaffRolesTab() {
  const alerts = useAlerts();
  // Any staff can view; only ManageStaffRoles can create/edit/delete/assign.
  const { hasStaffPermission } = useCurrentUser();
  const canManage = hasStaffPermission(StaffPermission.ManageStaffRoles);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<StaffRoleRow | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<StaffRoleRow | null>(null);
  const [roleToAssign, setRoleToAssign] = useState<StaffRoleRow | null>(null);

  const { data, loading } = useQuery(GET_STAFF_ROLES_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const refetchStaffRoles = {
    refetchQueries: [{ query: GET_STAFF_ROLES_QUERY }],
  };
  const [createStaffRole, { loading: creating }] = useMutation(
    CREATE_STAFF_ROLE,
    refetchStaffRoles
  );
  const [updateStaffRole, { loading: updating }] = useMutation(
    UPDATE_STAFF_ROLE,
    refetchStaffRoles
  );
  const [deleteStaffRole, { loading: deleting }] = useMutation(
    DELETE_STAFF_ROLE,
    refetchStaffRoles
  );

  const roles = useMemo(() => {
    return [...(data?.staffRoles ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  }, [data?.staffRoles]);

  const standardRoleId = useMemo(
    () => roles.find((r) => r.name === StaffRoleNamesEnum.standard)?.id,
    [roles]
  );

  const handleCreate = () => {
    setEditingRole(null);
    setEditorOpen(true);
  };

  const handleEdit = (role: StaffRoleRow) => {
    setEditingRole(role);
    setEditorOpen(true);
  };

  const handleSave = async ({ name, description, permissions }: StaffRoleSaveInput) => {
    try {
      if (editingRole) {
        await updateStaffRole({
          variables: { id: editingRole.id, name, description, permissions },
        });
        alerts.success("Staff role updated");
      } else {
        await createStaffRole({
          variables: { name, description, permissions },
        });
        alerts.success("Staff role created");
      }
      setEditorOpen(false);
      setEditingRole(null);
    } catch (error) {
      alerts.error(error instanceof Error ? error.message : "Failed to save staff role");
    }
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    try {
      await deleteStaffRole({ variables: { id: roleToDelete.id } });
      alerts.success("Staff role deleted");
    } catch (error) {
      alerts.error(error instanceof Error ? error.message : "Failed to delete staff role");
    } finally {
      setRoleToDelete(null);
    }
  };

  const columns: GridColDef<StaffRoleRow>[] = [
    { field: "name", headerName: "Role", flex: 1, minWidth: 200 },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 240,
      sortable: false,
    },
    {
      field: "userCount",
      headerName: "Users",
      width: 90,
      type: "number",
      sortable: false,
    },
    permissionsCountColumn<StaffRoleRow>({ width: 120 }),
  ];

  if (canManage) {
    columns.push({
      field: "actions",
      type: "actions",
      sortable: false,
      align: "right",
      width: 130,
      getActions: (params) => [
        <Tooltip title="Assign users" key="assign">
          <GridActionsCellItem
            onClick={() => setRoleToAssign(params.row)}
            label="Assign users"
            icon={<PersonAdd />}
          />
        </Tooltip>,
        <Tooltip title="Edit role" key="edit">
          <GridActionsCellItem
            onClick={() => handleEdit(params.row)}
            label="Edit role"
            icon={<Edit />}
          />
        </Tooltip>,
        <Tooltip title="Delete role" key="delete">
          <GridActionsCellItem
            onClick={() => setRoleToDelete(params.row)}
            label="Delete role"
            icon={<Delete />}
            disabled={deleting}
          />
        </Tooltip>,
      ],
    });
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="h6">Staff roles</Typography>
        {canManage && (
          <Button variant="contained" onClick={handleCreate}>
            Create staff role
          </Button>
        )}
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          height: roleGridHeight(roles.length),
          overflow: "hidden",
          borderRadius: 1,
        }}
      >
        <DataGrid<StaffRoleRow>
          rows={roles}
          loading={loading}
          getRowId={(row) => row.id}
          noRowsMessage="No staff roles yet."
          sx={{ border: "none" }}
          columns={columns}
        />
      </Paper>

      {editorOpen && (
        <StaffRoleEditorDialog
          role={editingRole}
          saving={creating || updating}
          onClose={() => {
            setEditorOpen(false);
            setEditingRole(null);
          }}
          onSave={handleSave}
        />
      )}

      {roleToAssign && (
        <AssignStaffUsersDialog
          role={roleToAssign}
          standardRoleId={standardRoleId}
          onClose={() => setRoleToAssign(null)}
          onAssigned={() => setRoleToAssign(null)}
        />
      )}

      <DeleteRoleConfirmDialog
        open={!!roleToDelete}
        onClose={() => setRoleToDelete(null)}
        header={`Delete staff role "${roleToDelete?.name}"?`}
        onConfirm={handleDelete}
        info="Roles that are still assigned to staff users can't be deleted until those users are reassigned."
        loading={deleting}
      />
    </Stack>
  );
}
